import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, Palette, Rocket, Loader2, Check, Monitor, Smartphone,
  GripVertical, ToggleLeft, ToggleRight, MapPin, Phone, Mail,
  Globe, Dog, Baby, Camera, Image, User, ExternalLink,
  Sparkles, Copy, CheckCircle, ChevronRight, ChevronDown, ArrowRight, PartyPopper, AlertTriangle, Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';
import PanelLayout from '@/components/PanelLayout';
import ContextPanel from '@/components/ContextPanel';
import { useAuth } from '@/context/AuthContext';
import { useMyViewStats } from '@/hooks/usePageViews';

const PAGE_TABS = [
  { id: 'preview', labelKey: 'pageBuilder.preview', icon: Eye },
  { id: 'content', labelKey: 'pageBuilder.colors', icon: Palette },
  { id: 'publish', labelKey: 'pageBuilder.publish', icon: Rocket },
];

const COLORS = ['#D6B36A', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

/* Load Google Fonts for preview */
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Cormorant+Garamond:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap";
if (typeof document !== 'undefined' && !document.querySelector(`link[href="${FONT_LINK}"]`)) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_LINK;
  document.head.appendChild(link);
}

const _heading = "'Playfair Display', 'Cormorant Garamond', Georgia, serif";
const _body = "'Lato', 'DM Sans', sans-serif";

/* ── Section Heading for preview ── */
function PreviewSectionHeading({ subtitle, title, primary, text, isMobile }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: isMobile ? 24 : 32 }}>
      {subtitle && (
        <p style={{ color: primary, fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', fontFamily: _body, marginBottom: 8 }}>
          {subtitle}
        </p>
      )}
      <h2 style={{ color: text, fontSize: isMobile ? 22 : 28, fontWeight: 600, fontFamily: _heading, lineHeight: 1.2, margin: 0 }}>
        {title}
      </h2>
      <div style={{ width: 40, height: 2, background: primary, margin: '12px auto 0', borderRadius: 2 }} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   FULL-WIDTH LANDING PREVIEW (sprzedazowy)
   ───────────────────────────────────────────────────── */
const HIGHLIGHT_LABELS = {
  zdrowie_badania: 'Zdrowie i badania', rodowod: 'Rodowód FCI/ZKwP', warunki: 'Warunki i opieka',
  doswiadczenie: 'Doświadczenie', kontakt_po: 'Kontakt po odbiorze', terminy: 'Szybkie terminy',
  jakosc: 'Dobra jakość', opinie: 'Opinie klientów', cena: 'Cena',
};
const formatHighlight = (h) => HIGHLIGHT_LABELS[h?.trim?.()?.toLowerCase?.()] || h;
const expandHighlights = (arr) => {
  if (!arr || !arr.length) return [];
  return arr.flatMap(h => typeof h === 'string' && h.includes(',') ? h.split(',').map(s => s.trim()).filter(Boolean) : [h]);
};

function LandingPreview({ pageData, previewData, viewport }) {
  const { theme = {}, sections = [] } = pageData || {};
  const profile = previewData?.profile || pageData?.profile || {};
  const dogs = previewData?.dogs || [];
  const litters = previewData?.litters || [];
  const isDark = theme.background === 'dark';
  const primary = theme.primary_color || '#D6B36A';
  const bg = isDark ? '#0B0F1A' : '#FAFAF8';
  const text = isDark ? '#FFFFFF' : '#1A1A1A';
  const muted = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(26,26,26,0.45)';
  const cardBg = isDark ? 'rgba(16,24,43,0.6)' : 'rgba(0,0,0,0.02)';
  const borderCol = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const isMobile = viewport === 'mobile';
  const w = isMobile ? 390 : '100%';

  const enabledSections = sections.filter(s => s.enabled).sort((a, b) => a.order - b.order);
  const mainPhoto = getImageUrl(profile.main_photo);
  const galleryPhotos = (profile.gallery || []).map(g => getImageUrl(g)).filter(Boolean);
  const ctaText = profile.business_type === 'breeder' ? 'Zapytaj o szczenieta' : 'Napisz do nas';

  return (
    <div style={{ width: w, maxWidth: isMobile ? 390 : '100%', margin: '0 auto' }}>
      <div
        className={`${isMobile ? 'rounded-2xl shadow-2xl' : 'rounded-2xl'} overflow-hidden border`}
        style={{ background: bg, fontFamily: _body, minHeight: 400, borderColor: borderCol }}
        data-testid="landing-preview"
      >
        {/* Navigation Bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '12px 20px' : '14px 40px', borderBottom: `1px solid ${borderCol}` }}>
          <span style={{ color: text, fontSize: isMobile ? 15 : 18, fontWeight: 600, fontFamily: _heading, letterSpacing: '0.02em' }}>
            {profile.business_name || 'Twoja nazwa'}
          </span>
          {isMobile ? (
            <Menu size={20} color={muted} />
          ) : (
            <nav style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              {enabledSections.filter(s => ['about', 'offer', 'gallery', 'contact'].includes(s.id)).map(s => (
                <span key={s.id} style={{ color: muted, fontSize: 11, fontWeight: 400, cursor: 'pointer', fontFamily: _body, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.label}</span>
              ))}
              {dogs.length > 0 && (
                <span style={{ color: muted, fontSize: 11, fontWeight: 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontFamily: _body, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Psy <ChevronDown size={10} />
                </span>
              )}
              {litters.length > 0 && (
                <span style={{ color: muted, fontSize: 11, fontWeight: 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontFamily: _body, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Mioty <ChevronDown size={10} />
                </span>
              )}
              {(profile.services || []).length > 0 && (
                <span style={{ color: muted, fontSize: 11, fontWeight: 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontFamily: _body, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Usługi <ChevronDown size={10} />
                </span>
              )}
            </nav>
          )}
        </div>

        {enabledSections.map(section => {
          switch (section.id) {

            /* ── HERO ── */
            case 'hero':
              return (
                <div key="hero" className="relative" style={{ minHeight: isMobile ? 280 : 360 }}>
                  <div className="absolute inset-0 overflow-hidden">
                    {mainPhoto
                      ? <img src={mainPhoto} alt="" className="w-full h-full object-cover" style={{ objectPosition: 'center 30%' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      : <div className="w-full h-full" style={{ background: isDark ? '#0B0F1A' : '#E8E4DF' }} />
                    }
                    <div className="absolute inset-0" style={{ background: isDark
                      ? 'linear-gradient(to top, rgba(11,15,26,0.88) 0%, rgba(11,15,26,0.35) 55%, rgba(11,15,26,0.1) 100%)'
                      : 'linear-gradient(to top, rgba(250,250,248,0.95) 0%, rgba(250,250,248,0.45) 45%, rgba(250,250,248,0.05) 100%)'
                    }} />
                  </div>
                  <div className="relative z-10" style={{ padding: isMobile ? '48px 24px 36px' : '72px 56px 48px' }}>
                    <div style={{ maxWidth: 580 }}>
                      {profile.highlights?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {expandHighlights(profile.highlights).slice(0, 3).map((h, i) => (
                            <span key={i} style={{ padding: '3px 10px', background: `${primary}18`, border: `1px solid ${primary}30`, borderRadius: 100, color: primary, fontSize: 10, fontWeight: 600, fontFamily: _body, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{formatHighlight(h)}</span>
                          ))}
                        </div>
                      )}
                      <h1 style={{ color: text, fontSize: isMobile ? 30 : 44, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em', margin: 0, fontFamily: _heading }}>
                        {profile.business_name || 'Twoja nazwa'}
                      </h1>
                      <p style={{ color: muted, fontSize: isMobile ? 14 : 16, marginTop: 12, lineHeight: 1.7, fontWeight: 300, fontFamily: _body }}>
                        {profile.description?.slice(0, 140) || 'Opis Twojej działalności — wypełnij profil, aby pojawił się tutaj.'}
                        {(profile.description?.length || 0) > 140 ? '...' : ''}
                      </p>
                      <div className="flex items-center gap-3 mt-6">
                        <button style={{ background: primary, color: '#fff', padding: isMobile ? '10px 22px' : '12px 30px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: _body, letterSpacing: '0.03em' }}>
                          {ctaText}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );

            /* ── ABOUT ── */
            case 'about':
              return profile.description ? (
                <div key="about" style={{ maxWidth: isMobile ? 340 : 700, margin: '0 auto', padding: isMobile ? '36px 24px' : '56px 56px' }}>
                  <PreviewSectionHeading subtitle="Poznaj nas" title="O nas" primary={primary} text={text} isMobile={isMobile} />
                  <p style={{ color: muted, fontSize: isMobile ? 13 : 15, lineHeight: 1.9, fontWeight: 300, fontFamily: _body, textAlign: 'center' }}>{profile.description}</p>
                  {profile.experience && <p style={{ color: muted, fontSize: isMobile ? 12 : 14, marginTop: 14, fontStyle: 'italic', opacity: 0.7, fontFamily: _body, textAlign: 'center', lineHeight: 1.8 }}>{profile.experience}</p>}
                </div>
              ) : null;

            /* ── OFFER ── */
            case 'offer':
              return profile.offer_description || profile.breeds?.length > 0 ? (
                <div key="offer" style={{ maxWidth: isMobile ? 340 : 700, margin: '0 auto', padding: isMobile ? '0 24px 36px' : '0 56px 56px' }}>
                  <PreviewSectionHeading subtitle="Co oferujemy" title="Oferta" primary={primary} text={text} isMobile={isMobile} />
                  {profile.offer_description && <p style={{ color: muted, fontSize: isMobile ? 13 : 15, lineHeight: 1.9, fontWeight: 300, fontFamily: _body, textAlign: 'center' }}>{profile.offer_description}</p>}
                  {profile.breeds?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16, justifyContent: 'center' }}>
                      {profile.breeds.map((b, i) => (
                        <span key={i} style={{ padding: '6px 14px', background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 8, color: text, fontSize: 13, fontWeight: 500, fontFamily: _body }}>{b}</span>
                      ))}
                    </div>
                  )}
                </div>
              ) : null;

            /* ── GALLERY ── */
            case 'gallery':
              return galleryPhotos.length > 0 ? (
                <div key="gallery" style={{ maxWidth: isMobile ? 340 : 800, margin: '0 auto', padding: isMobile ? '0 24px 36px' : '0 56px 56px' }}>
                  <PreviewSectionHeading subtitle="Zdjęcia" title="Galeria" primary={primary} text={text} isMobile={isMobile} />
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 10 }}>
                    {galleryPhotos.slice(0, 6).map((img, i) => (
                      <div key={i} style={{ height: isMobile ? 120 : 160, borderRadius: 10, overflow: 'hidden' }}>
                        <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;

            /* ── DOGS ── */
            case 'dogs':
              return dogs.length > 0 ? (
                <div key="dogs" style={{ maxWidth: isMobile ? 340 : 800, margin: '0 auto', padding: isMobile ? '0 24px 36px' : '0 56px 56px' }}>
                  <PreviewSectionHeading subtitle="Nasze psy" title={`Psy (${dogs.length})`} primary={primary} text={text} isMobile={isMobile} />
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                    {dogs.slice(0, 4).map((d, i) => (
                      <div key={i} style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 14, padding: 14, display: 'flex', gap: 14, alignItems: 'center' }}>
                        <div style={{ width: 56, height: 56, borderRadius: 10, background: `${primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                          {getImageUrl(d.main_photo) ? <img src={getImageUrl(d.main_photo)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Dog size={22} color={primary} />}
                        </div>
                        <div>
                          <p style={{ color: text, fontSize: 15, fontWeight: 600, fontFamily: _heading }}>{d.name}</p>
                          <p style={{ color: muted, fontSize: 13, fontFamily: _body }}>{d.breed}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;

            /* ── LITTERS ── */
            case 'litters':
              return litters.length > 0 ? (
                <div key="litters" style={{ maxWidth: isMobile ? 340 : 800, margin: '0 auto', padding: isMobile ? '0 24px 36px' : '0 56px 56px' }}>
                  <PreviewSectionHeading subtitle="Nasze mioty" title={`Mioty (${litters.length})`} primary={primary} text={text} isMobile={isMobile} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                    {litters.slice(0, 3).map((l, i) => (
                      <div key={i} style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 14, padding: 16, textAlign: 'center' }}>
                        <p style={{ color: text, fontSize: 15, fontWeight: 600, fontFamily: _heading }}>{l.name || `Miot ${l.breed}`}</p>
                        <p style={{ color: muted, fontSize: 13, marginTop: 4, fontFamily: _body }}>{l.breed} {l.birth_date ? `— ${l.birth_date}` : ''}</p>
                        {l.available_count > 0 && <p style={{ color: primary, fontSize: 13, fontWeight: 600, marginTop: 6, fontFamily: _body }}>{l.available_count} dostępnych</p>}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;

            /* ── CONTACT ── */
            case 'contact':
              return (
                <div key="contact" style={{ maxWidth: isMobile ? 340 : 700, margin: '0 auto', padding: isMobile ? '0 24px 36px' : '0 56px 56px' }}>
                  <PreviewSectionHeading subtitle="Skontaktuj się" title="Kontakt" primary={primary} text={text} isMobile={isMobile} />
                  <div style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 14, padding: isMobile ? 20 : 28, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                    {profile.phone && <p style={{ color: muted, fontSize: 15, display: 'flex', alignItems: 'center', gap: 10, fontFamily: _body }}><Phone size={18} color={primary} /> {profile.phone}</p>}
                    {profile.email && <p style={{ color: muted, fontSize: 15, display: 'flex', alignItems: 'center', gap: 10, fontFamily: _body }}><Mail size={18} color={primary} /> {profile.email}</p>}
                    {profile.city && <p style={{ color: muted, fontSize: 15, display: 'flex', alignItems: 'center', gap: 10, fontFamily: _body }}><MapPin size={18} color={primary} /> {[profile.city, profile.voivodeship].filter(Boolean).join(', ')}</p>}
                  </div>
                  <div style={{ textAlign: 'center', marginTop: 20 }}>
                    <button style={{ background: primary, color: '#fff', padding: '12px 28px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: _body, letterSpacing: '0.03em' }}>
                      {ctaText}
                    </button>
                  </div>
                </div>
              );

            default:
              return null;
          }
        })}

        {/* Footer with ApisAI branding */}
        <div style={{ padding: isMobile ? '16px 20px' : '20px 40px', borderTop: `1px solid ${borderCol}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, background: isDark ? 'rgba(0,0,0,0.2)' : '#FFFFFF' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/logo.svg" alt="ApisAI" style={{ width: 22, height: 22 }} onError={e => { e.target.style.display = 'none'; }} />
            <span style={{ color: text, fontSize: 13, fontWeight: 600, fontFamily: _heading }}>APISAI</span>
          </div>
          <span style={{ color: muted, fontSize: 11, fontWeight: 300, fontFamily: _body }}>Zamień swoją ofertę w klientów</span>
          <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, background: primary, padding: '6px 16px', borderRadius: 6, fontFamily: _body }}>ApisAI.pl</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────────────── */
export default function PageBuilder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [viewport, setViewport] = useState('desktop');
  const [justPublished, setJustPublished] = useState(false);
  const [copied, setCopied] = useState(false);
  const viewStats = useMyViewStats();

  const businessName = previewData?.profile?.business_name || pageData?.profile?.business_name || '';
  const isTestName = businessName && /test|_[a-f0-9]{4,}/i.test(businessName);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/page`, { headers }),
      axios.get(`${API}/page/preview-data`, { headers }),
    ]).then(([pageRes, dataRes]) => {
      setPageData(pageRes.data);
      setPreviewData(dataRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const savePage = useCallback(async (updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/page`, updates, { headers });
      setPageData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }, []); // eslint-disable-line

  const updateTheme = (key, value) => {
    const newTheme = { ...pageData.theme, [key]: value };
    setPageData(prev => ({ ...prev, theme: newTheme }));
    savePage({ theme: newTheme });
  };

  const toggleSection = (sectionId) => {
    const newSections = pageData.sections.map(s =>
      s.id === sectionId ? { ...s, enabled: !s.enabled } : s
    );
    setPageData(prev => ({ ...prev, sections: newSections }));
    savePage({ sections: newSections });
  };

  const moveSection = (idx, dir) => {
    const newSections = [...pageData.sections];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= newSections.length) return;
    [newSections[idx], newSections[targetIdx]] = [newSections[targetIdx], newSections[idx]];
    newSections.forEach((s, i) => s.order = i);
    setPageData(prev => ({ ...prev, sections: newSections }));
    savePage({ sections: newSections });
  };

  const publish = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/publish-page`, {}, { headers });
      setPageData(prev => ({ ...prev, status: 'published', slug: res.data.slug }));
      setJustPublished(true);
    } catch (e) {
      console.error('Publish error:', e);
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <PanelLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" /></div></PanelLayout>;
  }

  const status = pageData?.status || 'draft';
  const pageSlug = pageData?.slug || pageData?.profile?.business_name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'twoja-strona';
  const pageUrl = `${window.location.origin}/s/${pageSlug}`;
  const statusLabels = { draft: 'Nieopublikowana', ready: 'Gotowa', published: 'Opublikowana' };
  const statusColors = { draft: 'text-[#94A3B8] bg-zinc-50', ready: 'text-[#F47A3B] bg-[#F47A3B]/10', published: 'text-emerald-600 bg-emerald-50' };

  return (
    <PanelLayout>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
            <Globe className="w-3.5 h-3.5 text-[#F47A3B]" />
            <span className="text-[12px] font-semibold text-[#F47A3B]">Twoja strona sprzedażowa</span>
          </div>
          <h1 className="text-3xl font-extrabold text-zinc-900 mb-2" data-testid="page-builder-title">Strona</h1>
          <p className="text-[15px] text-zinc-500 max-w-md mx-auto">To tutaj trafiają klienci z Twojej promocji.</p>
          <div className="flex justify-center items-center gap-3 mt-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[status]}`} data-testid="page-status">
              {statusLabels[status]}
            </span>
            {saving && <span className="text-xs text-[#F47A3B] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Zapis...</span>}
          </div>
        </div>

        {/* Test name alert */}
        {isTestName && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-3 max-w-lg mx-auto" data-testid="test-name-alert">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-[13px] text-amber-700">
              <strong>Zmień nazwę na prawdziwą</strong> — klienci widzą ją na stronie.
            </p>
            <button onClick={() => navigate('/panel/profil')} className="text-[12px] font-semibold text-[#F47A3B] hover:underline flex-shrink-0">Zmień</button>
          </div>
        )}

        {/* Visit metric */}
        {status === 'published' && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-4"
          >
            <div className="inline-flex items-center gap-4 px-5 py-2.5 bg-white border border-zinc-200 rounded-xl" data-testid="page-visits">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-emerald-500" />
                <span className="text-[13px] text-zinc-700">
                  <b className="text-zinc-900">{viewStats.isLoading ? '...' : viewStats.summary.weekly_views}</b> osób zobaczyło Twoją ofertę (7 dni)
                </span>
              </div>
              <div className="w-px h-4 bg-zinc-200" />
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-zinc-500">
                  <b className="text-zinc-700">{viewStats.isLoading ? '...' : viewStats.summary.total_views}</b> łącznie
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* 3 Tabs — underline style */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8">
        <div className="flex gap-6 mb-6 border-b border-zinc-200" data-testid="page-tabs">
          {PAGE_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setJustPublished(false); }}
                data-testid={`page-tab-${tab.id}`}
                className={`flex items-center gap-2 px-1 pb-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[#F47A3B]'
                    : 'text-zinc-400 hover:text-zinc-700'
                }`}
              >
                <Icon className="w-4 h-4" />{t(tab.labelKey)}
                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F47A3B] rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* ═══ TAB: PODGLAD ═══ */}
        {activeTab === 'preview' && (
          <div data-testid="tab-preview">
            {/* Guide for users without photos */}
            {(!previewData?.profile?.main_photo && !previewData?.profile?.gallery?.length) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="guide-no-photos">
                <p className="text-[13px] font-bold text-amber-800 mb-1">Brak zdjęć — dodaj je teraz</p>
                <p className="text-[12px] text-amber-700 mb-3">Klienci kupują oczami. Strona ze zdjęciami dostaje 3x więcej zapytań.</p>
                <button onClick={() => navigate('/panel/profil')} className="px-4 py-2 bg-amber-500 text-white text-[12px] font-bold rounded-lg hover:bg-amber-600 transition-colors">
                  Dodaj zdjęcia w Profilu
                </button>
              </div>
            )}
            <div className="flex items-center justify-center gap-2 mb-5">
              <button
                onClick={() => setViewport('desktop')}
                className={`p-2 rounded-lg transition ${viewport === 'desktop' ? 'bg-[#F47A3B]/10 text-[#F47A3B]' : 'text-[#94A3B8] hover:text-zinc-900'}`}
                data-testid="viewport-desktop"
              ><Monitor className="w-5 h-5" /></button>
              <button
                onClick={() => setViewport('mobile')}
                className={`p-2 rounded-lg transition ${viewport === 'mobile' ? 'bg-[#F47A3B]/10 text-[#F47A3B]' : 'text-[#94A3B8] hover:text-zinc-900'}`}
                data-testid="viewport-mobile"
              ><Smartphone className="w-5 h-5" /></button>
            </div>
            <LandingPreview pageData={pageData} previewData={previewData} viewport={viewport} />
          </div>
        )}

        {/* ═══ TAB: TRESC I WYGLAD (merged theme + sections) ═══ */}
        {activeTab === 'content' && (
          <div className="grid gap-6 lg:grid-cols-2" data-testid="tab-content">
            <div className="space-y-5">
              {/* Color */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-5">
                <h3 className="text-zinc-900 font-semibold mb-3 text-[15px]">Kolor główny</h3>
                <div className="flex flex-wrap gap-2.5" data-testid="color-picker">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => updateTheme('primary_color', c)}
                      className={`w-9 h-9 rounded-xl transition-transform ${pageData?.theme?.primary_color === c ? 'ring-2 ring-[#1A1D26] scale-110' : 'hover:scale-105'}`}
                      style={{ background: c }}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </div>

              {/* Background */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-5">
                <h3 className="text-zinc-900 font-semibold mb-3 text-[15px]">Tło</h3>
                <div className="flex gap-3" data-testid="bg-picker">
                  {[
                    { id: 'dark', label: 'Ciemne', bg: '#0B0F1A', text: '#fff' },
                    { id: 'light', label: 'Jasne', bg: '#FAFAF8', text: '#1A1A1A' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => updateTheme('background', opt.id)}
                      className={`flex-1 rounded-xl p-3.5 text-center border transition ${
                        pageData?.theme?.background === opt.id ? 'border-[#F47A3B] ring-1 ring-[#F47A3B]/30' : 'border-zinc-200'
                      }`}
                      style={{ background: opt.bg, color: opt.text }}
                      data-testid={`bg-${opt.id}`}
                    >
                      <span className="text-sm font-medium">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Style */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-5">
                <h3 className="text-zinc-900 font-semibold mb-3 text-[15px]">Styl</h3>
                <div className="flex gap-0 border-b border-zinc-200" data-testid="style-picker">
                  {['minimal', 'premium', 'classic'].map(s => (
                    <button
                      key={s}
                      onClick={() => updateTheme('style', s)}
                      className={`flex-1 px-3 pb-3 text-sm font-medium transition-colors relative ${
                        pageData?.theme?.style === s
                          ? 'text-[#F47A3B]'
                          : 'text-zinc-400 hover:text-zinc-700'
                      }`}
                      data-testid={`style-${s}`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                      {pageData?.theme?.style === s && (
                        <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#F47A3B] rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sections */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-5">
                <h3 className="text-zinc-900 font-semibold mb-1.5 text-[15px]">Sekcje strony</h3>
                <p className="text-zinc-400 text-xs mb-4">Włącz/wyłącz sekcje i zmień kolejność.</p>
                <div className="space-y-2" data-testid="sections-list">
                  {(pageData?.sections || []).sort((a, b) => a.order - b.order).map((section, idx) => {
                    const isConditional = (section.id === 'dogs' && !pageData.has_dogs) || (section.id === 'litters' && !pageData.has_litters);
                    return (
                      <div
                        key={section.id}
                        className={`flex items-center gap-2.5 p-2.5 rounded-xl border transition ${
                          section.enabled ? 'bg-white border-zinc-200' : 'bg-[#F8FAFC] border-zinc-200 opacity-50'
                        }`}
                        data-testid={`section-${section.id}`}
                      >
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="text-[#94A3B8] hover:text-zinc-900 disabled:opacity-20 transition">
                            <ChevronRight className="w-3 h-3 -rotate-90" />
                          </button>
                          <button onClick={() => moveSection(idx, 1)} disabled={idx === pageData.sections.length - 1} className="text-[#94A3B8] hover:text-zinc-900 disabled:opacity-20 transition">
                            <ChevronRight className="w-3 h-3 rotate-90" />
                          </button>
                        </div>
                        <GripVertical className="w-3.5 h-3.5 text-zinc-300 flex-shrink-0" />
                        <span className="flex-1 text-[13px] text-zinc-900 font-medium">{section.label}</span>
                        {isConditional && <span className="text-[9px] text-[#94A3B8] bg-zinc-50 px-1.5 py-0.5 rounded">brak danych</span>}
                        <button onClick={() => toggleSection(section.id)} data-testid={`toggle-${section.id}`}>
                          {section.enabled
                            ? <ToggleRight className="w-5 h-5 text-[#F47A3B]" />
                            : <ToggleLeft className="w-5 h-5 text-zinc-500" />
                          }
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Live preview (side) */}
            <div className="bg-[#F8FAFC] border border-zinc-200 rounded-2xl p-4 overflow-hidden" style={{ maxHeight: 700 }}>
              <p className="text-xs text-zinc-400 mb-3 text-center font-medium">Podgląd na żywo</p>
              <div style={{ overflow: 'auto', maxHeight: 640 }}>
                <LandingPreview pageData={pageData} previewData={previewData} viewport="mobile" />
              </div>
            </div>
          </div>
        )}

        {/* ═══ TAB: PUBLIKACJA ═══ */}
        {activeTab === 'publish' && (
          <div className="max-w-2xl mx-auto" data-testid="tab-publish">
            <AnimatePresence mode="wait">
              {/* ── Post-publish celebration ── */}
              {(status === 'published' || justPublished) ? (
                <motion.div
                  key="published"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="bg-white border border-zinc-200 rounded-2xl p-8 text-center mb-5" data-testid="post-publish-celebration">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                      className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center mb-5"
                    >
                      <PartyPopper className="w-8 h-8 text-emerald-500" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-zinc-900 mb-1.5">Twoja strona jest gotowa!</h2>
                    <p className="text-zinc-500 text-[15px] mb-2">Teraz czas na klientów</p>

                    {/* URL bar */}
                    <div className="bg-[#F8FAFC] rounded-xl p-3.5 mb-6 flex items-center justify-between gap-3">
                      <p className="text-[#F47A3B] font-medium text-sm truncate" data-testid="page-url">{pageUrl}</p>
                      <button
                        onClick={() => copyUrl(pageUrl)}
                        className="flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-500 transition flex-shrink-0"
                        data-testid="copy-url-btn"
                      >
                        {copied ? <><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> Skopiowano!</> : <><Copy className="w-3.5 h-3.5" /> Kopiuj</>}
                      </button>
                    </div>

                    {/* 2 Big Tiles */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="post-publish-tiles">
                      {/* Tile: Wyślij do grup */}
                      <button
                        onClick={() => navigate('/panel/grupy')}
                        className="group relative p-6 rounded-2xl border-2 border-[#F47A3B]/20 bg-[#F47A3B]/5 hover:border-[#F47A3B] hover:shadow-lg transition-all text-left"
                        data-testid="tile-promote"
                      >
                        <div className="w-12 h-12 rounded-xl bg-[#F47A3B] flex items-center justify-center mb-4">
                          <Rocket className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-[16px] font-bold text-zinc-900 mb-1">Wyślij do grup</h3>
                        <p className="text-[13px] text-zinc-500 leading-relaxed">Opublikuj ofertę w grupach na Facebooku i zdobądź pierwsze zapytania.</p>
                        <ArrowRight className="absolute top-6 right-6 w-5 h-5 text-[#F47A3B] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>

                      {/* Tile: Zobacz */}
                      <a
                        href={`/s/${pageSlug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative p-6 rounded-2xl border-2 border-zinc-200 bg-white hover:border-[#3B82F6]/40 hover:shadow-lg transition-all text-left"
                        data-testid="tile-view"
                      >
                        <div className="w-12 h-12 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-4">
                          <ExternalLink className="w-6 h-6 text-[#3B82F6]" />
                        </div>
                        <h3 className="text-[16px] font-bold text-zinc-900 mb-1">Zobacz stronę</h3>
                        <p className="text-[13px] text-zinc-500 leading-relaxed">Otwórz stronę w nowej karcie i sprawdź jak wygląda.</p>
                        <ArrowRight className="absolute top-6 right-6 w-5 h-5 text-[#3B82F6] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              ) : (
                /* ── Pre-publish ── */
                <motion.div
                  key="draft"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-white border border-zinc-200 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[#F47A3B]/10 flex items-center justify-center mb-5">
                      <Rocket className="w-8 h-8 text-[#F47A3B]" />
                    </div>
                    <h2 className="text-2xl font-bold text-zinc-900 mb-2">Opublikuj stronę</h2>
                    <p className="text-zinc-500 text-sm mb-3">Twoja strona jest gotowa — wygenerowana automatycznie z Twojego profilu.</p>

                    {/* Pre-publish checklist */}
                    <div className="space-y-2 mb-5 text-left">
                      {[
                        { done: !!(previewData?.profile?.business_name), label: 'Nazwa firmy/hodowli', fix: '/panel/profil' },
                        { done: !!(previewData?.profile?.main_photo || previewData?.profile?.gallery?.length), label: 'Zdjęcia dodane', fix: '/panel/profil' },
                        { done: !!(previewData?.profile?.description), label: 'Opis uzupełniony', fix: '/panel/profil' },
                        { done: !!(previewData?.profile?.phone || previewData?.profile?.email), label: 'Dane kontaktowe', fix: '/panel/profil' },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-zinc-50">
                          {item.done
                            ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                            : <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />}
                          <span className={`text-[13px] flex-1 ${item.done ? 'text-zinc-600' : 'text-amber-700 font-medium'}`}>{item.label}</span>
                          {!item.done && <button onClick={() => navigate(item.fix)} className="text-[11px] text-[#F47A3B] font-bold hover:underline shrink-0">Uzupełnij</button>}
                        </div>
                      ))}
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl mb-6 text-left">
                      <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-zinc-500">
                        <p className="font-medium text-emerald-500 mb-1">Strona zoptymalizowana pod SEO</p>
                        <p>Tytuły, opisy, meta tagi — wszystko gotowe. Google zobaczy Twoją stronę od razu.</p>
                      </div>
                    </div>

                    <Button
                      onClick={publish}
                      disabled={saving}
                      size="lg"
                      className="bg-[#F47A3B] hover:bg-[#E06A2B] w-full rounded-xl text-white font-semibold"
                      data-testid="publish-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
                      Opublikuj stronę
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* ═══ UPSELL — Strona premium ═══ */}
        <div className="mt-8 bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-2xl p-6 border border-zinc-700" data-testid="premium-upsell">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles className="w-5 h-5 text-[#F47A3B]" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-[15px] mb-1">Potrzebujesz bardziej rozbudowanej strony?</h3>
              <p className="text-zinc-400 text-[13px] mb-4">Możemy przygotować ją dla Ciebie indywidualnie — z własnym designem, domeną i zaawansowanymi funkcjami.</p>
              <a
                href="https://wa.me/48507334540?text=Dzie%C5%84%20dobry%2C%20jestem%20zainteresowana%20stworzeniem%20bardziej%20rozbudowanej%20strony."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#F47A3B] hover:bg-[#E06A2B] text-white text-sm font-semibold transition-colors"
                data-testid="premium-upsell-cta"
              >
                <ArrowRight className="w-4 h-4" />
                Zapytaj o stronę premium
              </a>
            </div>
          </div>
        </div>
        </div>

          {/* RIGHT: Context panel — same design as Profil */}
          <ContextPanel
            title={t('pageBuilder.contextTitle')}
            desc={t('pageBuilder.contextDesc')}
            benefits={[
              t('pageBuilder.benefit1'),
              t('pageBuilder.benefit2'),
              t('pageBuilder.benefit3'),
            ]}
            action={t('pageBuilder.contextAction')}
            link={{ to: '/panel/profil', title: t('common.back'), subtitle: t('pageBuilder.contextDesc') }}
            icon={Eye}
          />
        </div>

      </div>
    </PanelLayout>
  );
}
